import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { invalidateAccountBalances, beginSync, endSync } from '../hooks/useAccountBalances';
import { Loader2, CheckCircle, XCircle, Trash2, Download } from 'lucide-react';
import YearSelector from './YearSelector';
import ExportButton from './ExportButton';
import { useYear } from '../contexts/YearContext';

// Custom Confirmation Modal Component
function ConfirmDialog({ isOpen, onConfirm, onCancel, title, message }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
        <div className="flex space-x-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function WeeklyTracker() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, entryId: null });
  const [plan, setPlan] = useState(null);
  const { selectedYear } = useYear();

  // Memoize per-bucket YTD totals so they're only recomputed when `entries`
  // (or the plan's custom allocations) change — not on every render.
  const ytdTotals = useMemo(() => {
    const customSums = {};
    let income = 0, taxes = 0, business = 0, personal = 0, savings = 0, buffer = 0;
    for (const e of entries) {
      income   += (e.income || 0) + (e.overrides || 0);
      taxes    += e.taxes    || 0;
      business += e.business || 0;
      personal += e.personal || 0;
      savings  += e.savings  || 0;
      buffer   += e.buffer   || 0;
      for (const [name, amt] of Object.entries(e.custom_allocations || {})) {
        customSums[name] = (customSums[name] || 0) + (amt || 0);
      }
    }
    const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return {
      income: fmt(income), taxes: fmt(taxes), business: fmt(business),
      personal: fmt(personal), savings: fmt(savings), buffer: fmt(buffer),
      custom: Object.fromEntries(Object.entries(customSums).map(([n, v]) => [n, fmt(v)])),
    };
  }, [entries]);

  useEffect(() => {
    loadPlan();
    // `loadPlan` is stable — declared above without deps and only updates
    // `plan` state. Inlining it or wrapping in useCallback adds complexity
    // without behaviour change. Mount-only fetch.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadEntries();
    }
    // `loadEntries` closes over `selectedYear` via `${API}/income/weekly?year=${selectedYear}`
    // and we re-run whenever that changes — which is all this effect needs.
    // eslint-disable-next-line
  }, [selectedYear]);

  const loadPlan = async () => {
    try {
      const response = await axios.get(`${API}/financial-plan`);
      setPlan(response.data);
    } catch (err) {
      console.error('Error loading financial plan:', err);
    }
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/income/weekly?year=${selectedYear}`);
      setEntries(response.data);
    } catch (err) {
      console.error('Error loading weekly entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id) => {
    beginSync('Deleting entry');
    try {
      await axios.delete(`${API}/income/weekly/${id}`);
      await loadEntries();
      invalidateAccountBalances();
      endSync({ success: true });
    } catch (err) {
      endSync({ success: false });
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error deleting entry:', err);
      }
      alert('Failed to delete entry: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmDialog({ isOpen: true, entryId: id });
  };

  const handleConfirmDelete = () => {
    const { entryId } = confirmDialog;
    setConfirmDialog({ isOpen: false, entryId: null });
    deleteEntry(entryId);
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ isOpen: false, entryId: null });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tracker-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="weekly-tracker">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:text-white">52-Week Tracker</h1>
          <div className="flex items-center space-x-4">
            <YearSelector />
            {entries.length > 0 && (
              <ExportButton
                endpoint="/exports/weekly-income.csv"
                params={{ year: selectedYear }}
                filename={`papertrail-weekly-income-${selectedYear}.csv`}
                label="Export CSV"
                testId="export-weekly-income-csv-btn"
              />
            )}
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-300 dark:text-gray-300">
          Your weekly income entries for {selectedYear}. Each row shows the allocation <strong>snapshot at the time that paycheck was logged</strong> — it reflects your plan percentages on that entry&apos;s pay date. These numbers are historical records, not live balances.
        </p>
      </div>

      {entries.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center" data-testid="no-entries">
          <p className="text-gray-900 dark:text-gray-100 text-lg font-semibold">No weekly income entries yet. Start by logging your first paycheck!</p>
          <a
            href="/calculator"
            className="inline-block mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Log Weekly Income
          </a>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tracker-table">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Week #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Pay Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Income</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase" title="Allocation snapshot — taxes % of this entry's income">Taxes ({plan?.allocation?.taxes ?? 20}%)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Business ({plan?.allocation?.business ?? 20}%)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Personal ({plan?.allocation?.personal ?? 55}%)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Savings ({plan?.allocation?.savings ?? 5}%)</th>
                  {/* Custom allocations columns */}
                  {plan?.allocation?.custom_allocations?.map((alloc) => (
                    <th key={alloc.name} className="px-4 py-3 text-right text-xs font-semibold uppercase">{alloc.name} ({alloc.percentage}%)</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase" title="Buffer snapshot at time of entry — unallocated capital retained outside plan buckets">Buffer</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Bus. Req</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Pers. Req</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase" title="Personal allocation minus this entry's personal-bill requirement (snapshot, not live)">Personal Net After Bills</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Notes</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                {entries.map((entry, index) => (
                  <tr key={entry.id} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'} data-testid={`tracker-row-${entry.week_number}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white dark:text-white">{entry.week_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">
                      {new Date(entry.pay_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                      ${(entry.income + (entry.overrides || 0)).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 text-right">
                      ${entry.taxes.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 text-right">
                      ${entry.business.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 text-right">
                      ${entry.personal.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 text-right">
                      ${entry.savings.toFixed(2)}
                    </td>
                    {/* Custom allocations values */}
                    {plan?.allocation?.custom_allocations?.map((alloc) => (
                      <td key={alloc.name} className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 text-right">
                        ${(entry.custom_allocations?.[alloc.name] || 0).toFixed(2)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-sm text-right">
                      {entry.buffer > 0 ? (
                        <span className="font-bold text-blue-600">${entry.buffer.toFixed(2)}</span>
                      ) : (
                        <span className="text-gray-400">$0.00</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                          entry.business_req_met
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                        data-testid={`business-req-${entry.week_number}`}
                      >
                        {entry.business_req_met ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> YES</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> NO</>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                          entry.personal_req_met
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                        data-testid={`personal-req-${entry.week_number}`}
                      >
                        {entry.personal_req_met ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> YES</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> NO</>
                        )}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-sm font-bold text-right ${
                      entry.net_after_expenses >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${entry.net_after_expenses.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate">
                      {entry.notes || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDeleteClick(entry.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-entry-${entry.week_number}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {entries.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Allocation Snapshots Total</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Sum of the persisted allocation values for each entry at the time it was logged — <strong>not live balances</strong>.
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-1">
              📊 Allocation Snapshots at Time of Entry
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              These totals are the sum of the <strong>split values stored on each weekly entry</strong> using the allocation plan that was active when it was logged. For live Expected Current Balances (allocated − expenses), use <a href="/accounts" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">Account Balances</a>; for YTD plan distribution use <a href="/reports" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">Reports</a>.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Total Income Logged</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                ${ytdTotals.income}
              </p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Tax Snapshot ({plan?.allocation?.taxes ?? 20}%)</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">Sum of per-entry tax splits</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">
                ${ytdTotals.taxes}
              </p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Business Snapshot ({plan?.allocation?.business ?? 20}%)</p>
              <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                ${ytdTotals.business}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Personal Snapshot ({plan?.allocation?.personal ?? 55}%)</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">
                ${ytdTotals.personal}
              </p>
            </div>
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Savings Snapshot ({plan?.allocation?.savings ?? 5}%)</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">Sum of per-entry savings splits</p>
              <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                ${ytdTotals.savings}
              </p>
            </div>
            <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Buffer Snapshot</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">Unallocated capital held outside plan buckets</p>
              <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                ${ytdTotals.buffer}
              </p>
            </div>
            {/* Custom allocations summary */}
            {plan?.allocation?.custom_allocations?.map((alloc) => (
              <div key={alloc.name} className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">{alloc.name} Snapshot ({alloc.percentage}%)</p>
                <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                  ${ytdTotals.custom[alloc.name] || '0.00'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Entry"
        message="Are you sure you want to delete this weekly income entry? This action cannot be undone."
      />
    </div>
  );
}

export default WeeklyTracker;
